import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { SITE_URL } from "../lib/constants";
import { useSEO, buildBreadcrumbsJsonLd } from "../lib/seo";
import { useSiteTexts } from "../lib/siteTexts";
import { PAGE_TEXTS } from "../data/pageTexts";
import { imgSet } from "../data/portfolio";
import { CrossLink, PortfolioHero } from "../components/portfolio/PortfolioHero";
import { InspirationCard, InspirationBadge } from "../components/portfolio/InspirationCard";
import { InspirationFilters, matchInspiration } from "../components/portfolio/InspirationFilters";
import { InspirationLightbox } from "../components/portfolio/InspirationLightbox";
import { TextBody } from "../components/info/TextBody";
import { AdviceEndCta } from "../components/info/AdviceBlocks";

const T = PAGE_TEXTS.inspiratie.blocks;
const PATH = "/projecten/inspiratie";
const CRUMBS = [{ label: "Home", href: "/" }, { label: "Projecten", href: "/projecten" }, { label: "Inspiratie", href: PATH }];
const abs = (p) => (p.startsWith("/") ? SITE_URL + p : p);

const galleryLd = (seo, items) => ({
  "@context": "https://schema.org", "@type": "ImageGallery", name: seo.title, description: seo.body, url: SITE_URL + PATH,
  associatedMedia: items.slice(0, 30).map((it) => ({ "@type": "ImageObject", contentUrl: abs(it.image.path), name: it.title, caption: it.caption, creditText: it.credit || undefined })),
});

const Mosaic = ({ items }) => (
  <ul className="grid grid-cols-2 gap-3" aria-hidden="true">
    {items.slice(0, 4).map((it, i) => (
      <li key={it.id} className={`relative overflow-hidden rounded-2xl bg-slate-100 ${i % 2 ? "translate-y-5" : ""}`}>
        <img src={it.image.path} srcSet={imgSet(it.image.path)} sizes="260px" alt="" className="aspect-[4/5] w-full object-cover" loading={i < 2 ? "eager" : "lazy"} />
        <InspirationBadge it={it} className="absolute left-2 top-2 !text-[10.5px]" />
      </li>
    ))}
  </ul>
);

export default function Inspiratie() {
  const t = useSiteTexts("inspiratie");
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/inspiration").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const seo = t(T.seo);
  useSEO({ title: seo.title, description: seo.body, path: PATH, jsonLd: galleryLd(seo, items || []), breadcrumbs: buildBreadcrumbsJsonLd(CRUMBS) });
  const shown = useMemo(() => (items || []).filter((it) => matchInspiration(it, filters)), [items, filters]);
  const hero = t(T.hero);
  const guide = t(T.guide);

  return (
    <div data-testid="inspiratie-page">
      <PortfolioHero crumbs={CRUMBS} eyebrow="Inspiratiegalerij" title={hero.title} lead={hero.body} testid="inspiration-hero" aside={items?.length ? <Mosaic items={items} /> : null} />
      <section aria-labelledby="galerij-title" className="section">
        <div className="container-tight">
          <h2 id="galerij-title" className="sr-only">Inspiratiebeelden</h2>
          {items === null ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-maas" /></div> : (
            <>
              <InspirationFilters items={items} filters={filters} setFilters={setFilters} count={shown.length} />
              {shown.length ? (
                <ul className="mt-8 columns-1 gap-6 sm:columns-2 lg:columns-3" data-testid="inspiration-grid">
                  {shown.map((it) => <InspirationCard key={it.id} it={it} onOpen={setOpen} />)}
                </ul>
              ) : <p className="mt-10 text-center text-slate-500" data-testid="inspiration-empty">Geen beelden met deze combinatie. Pas de filters aan.</p>}
            </>
          )}
        </div>
      </section>
      <section aria-labelledby="gids-title" className="section-tight border-y border-slate-200 bg-slate-50">
        <div className="container-tight grid gap-8 lg:grid-cols-12">
          <h2 id="gids-title" className="h2 lg:col-span-5">{guide.title}</h2>
          <TextBody text={guide.body} className="lg:col-span-7" testid="inspiration-guide" />
        </div>
      </section>
      <CrossLink to="/projecten" text="Benieuwd naar werk dat wij zelf hebben uitgevoerd?" label="Bekijk uitgevoerde projecten" testid="inspiration-projects-link" className="section-tight" />
      <AdviceEndCta title="Een uitvoering gezien die bij uw woning past?" location="inspiratie" />
      <InspirationLightbox it={open} onClose={() => setOpen(null)} />
    </div>
  );
}
